import { isValidArray, isValidObject } from "../../Services/validators";
import {
  alignPhoneNumber,
  capitalizeFirstLetter,
  dateAndTimeConverter,
  isValidDate
} from "./Constants";

export function getLocationList(locations) {
  if (!isValidObject(locations)) {
    return [];
  }

  return Object.values(locations)
    .sort((a, b) => {
      const nameA = parseInt(a.PCTNo) || 0;
      const nameB = parseInt(b.PCTNo) || 0;
      return nameA - nameB;
    })
    .map(({ PCTNo, location }) => `${PCTNo || "N/A"} - ${location || "N/A"}`);
}

export const procedureHistoryFilterData = (props) => [
  {
    key: "type",
    title: "TYPE",
    multiple: true,
    options: ["Employee", "Logistics", "Sensor", "Independent Engineer"]
  },
  {
    key: "dateRange",
    title: "DATE RANGE",
    multiple: false,
    options: ["Lifetime", "Today", "Last 7 days", "Last 30 days", "Select Date"]
  },
  {
    key: "assignedTo",
    multiple: false,
    options: ["Select Employee"],
    title: "ASSIGNED TO"
  },
  {
    key: "procedureName",
    multiple: true,
    options: props?.proceduresName || [],
    title: "NAME"
  },
  {
    key: "PCTNo",
    multiple: true,
    search: true,
    searchData: {
      label: "Search PCT / Address"
    },
    options: getLocationList(props?.locations),
    title: "LOCATIONS"
  }
];

export const defaultProcedureHistoryFilter = {
  type: [],
  dateRange: {
    type: "Today",
    from: new Date().toISOString().split("T")[0],
    to: new Date().toISOString().split("T")[0]
  },
  assignedTo: "",
  procedureName: [],
  PCTNo: []
};

export const procedureHistoryTableHeadings = [
  { title: "SI.No", width: "80px", textAlign: "left" },
  { title: "PCT #", width: "80px", textAlign: "left" },
  { title: "NAME", width: "220px", textAlign: "left" },
  { title: "TYPE", width: "100px", textAlign: "left" },
  // { title: "PROGRESSION", width: "100px", textAlign: "left" },
  { title: "COMPLETED BY", width: "180px", textAlign: "left" },
  { title: "CREATED AT", width: "180px", textAlign: "right" }
];

const getStepsFromLastUpdate = (latestUpdate) => {
  return latestUpdate.reduce(
    (acc, obj) => {
      if (obj.success) {
        acc.success++;
      }
      acc.total++;
      return acc;
    },
    { success: 0, total: 0 }
  );
};

export function convertProcedureHistoryToTableData(procedureHistory) {
  const procedureHistoryTableData = [];

  for (let i = 0; i < procedureHistory.length; i++) {
    const item = procedureHistory[i];
    const transformedItem = {
      ...item,
      serialNo: i + 1,
      documentId: item?.documentId,
      PCTNo: item?.location?.PCTNo?.padStart(2, "0") || "N/A",
      name: item?.procedure?.title || "N/A",
      type: capitalizeFirstLetter(item?.procedure?.type) || "N/A",
      progression: `${getStepsFromLastUpdate(item.latestUpdate).success} / ${
        getStepsFromLastUpdate(item.latestUpdate).total
      }`,
      completedBy: item?.employee
        ? `${alignPhoneNumber(item?.employee?.phoneNumber)}, ${
            item?.employee?.name
          }`
        : "N/A",
      createdAt: `${dateAndTimeConverter(
        item.createdAt,
        "Time"
      )}, ${dateAndTimeConverter(item.createdAt, "thirdDate")}`
    };
    procedureHistoryTableData.push(transformedItem);
  }

  return procedureHistoryTableData;
}

export function procedureHistoryTableField(data, type) {
  switch (data) {
    case "SI.No":
      return "serialNo";
    case "PCT #":
      return type === "sort" ? "location.PCTNo" : "PCTNo";
    case "NAME":
      return type === "sort" ? "procedure.title" : "name";
    case "COMPLETED BY":
      return type === "sort" ? "employee.phoneNumber" : "completedBy";
    case "PROGRESSION":
      return type === "sort" ? "employee.phoneNumber" : "progression";
    case "TYPE":
      return type === "sort" ? "procedure.type" : "type";
    case "CREATED AT":
      return "createdAt";
    default:
      return data?.toLowerCase();
  }
}

export const procedureHistoryQueryPath = {
  dateRange: "createdAt",
  type: "procedure.type",
  assignedTo: "employee.phoneNumber",
  procedureName: "procedure.title",
  PCTNo: "location.id"
};

export const procedureHistoryQueryBuilder = (filterData, locationData) => {
  const query = [];

  const addQuery = (path, operator, value) => {
    query.push(path, operator, value);
  };

  const addRangeQuery = (path, from, to) => {
    addQuery(path, ">=", +new Date(`${from}:00:00:00`));
    addQuery(path, "<=", +new Date(`${to}:23:59:59`));
  };

  Object.entries(filterData).forEach(([key, value]) => {
    if (!value) {
      return;
    }

    switch (key) {
      case "dateRange":
        if (
          value.from &&
          isValidDate(value.from) &&
          value.to &&
          isValidDate(value.to)
        ) {
          addRangeQuery(procedureHistoryQueryPath[key], value.from, value.to);
        }
        break;

      case "assignedTo":
        if (value) {
          addQuery(procedureHistoryQueryPath[key], "in", [
            value?.includes("@") ? value : `+91${value}`
          ]);
        }
        break;

      case "type":
        if (isValidArray(value)) {
          const typesData = {
            Employee: "EMPLOYEE",
            Sensor: "SENSOR",
            Logistics: "LOGISTICS",
            "Independent Engineer": "independentEngineer"
          };
          const types = value.map((element) => typesData[element]);
          addQuery(procedureHistoryQueryPath[key], "in", types);
        }
        break;

      case "PCTNo":
        if (locationData && isValidArray(value)) {
          const locationIds = value
            .map(
              (data) =>
                Object.values(locationData).find(
                  (location) => location.PCTNo === data.split(" - ")[0]
                )?.documentId
            )
            .filter(Boolean);
          if (isValidArray(locationIds)) {
            addQuery(procedureHistoryQueryPath[key], "in", locationIds);
          }
        }
        break;

      default:
        if (isValidArray(value)) {
          addQuery(procedureHistoryQueryPath[key], "in", value);
        }
        break;
    }
  });
  return query;
};
